import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Bot } from "lucide-react";
import { API } from "../lib/api";

const sessionId = "sawd-" + Math.random().toString(36).slice(2);

const QUICK_ACTIONS = [
  { label: "🔍 Free Growth Scan", to: "/ai-business-growth" },
  { label: "🚀 Website Build $299", to: "/website-build" },
  { label: "🛡️ Add Hosting", to: "/accelerator" },
  { label: "🎯 Find My Template", to: "/template-recommender" },
];

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I'm Alamo 👋 Tell me about your business and I'll show you how we can generate more leads & sales." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const loc = useLocation();
  const scroller = useRef(null);

  useEffect(() => {
    scroller.current?.scrollTo(0, scroller.current.scrollHeight);
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, context: loc.pathname }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble connecting. Please try again or call +1 210-239-7606." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="chatbot-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-xl shadow-primary/40 hover:scale-105 transition-transform"
        aria-label="Open sales chat">
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="chatbot-panel"
          className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[520px] rounded-3xl glass shadow-2xl flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-3 bg-primary/10">
            <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center"><Bot className="h-5 w-5" /></div>
            <div>
              <p className="font-head font-bold leading-none">Alamo</p>
              <p className="text-xs text-muted-foreground">Sales assistant · online</p>
            </div>
          </div>
          <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                  {m.content || <span className="opacity-60">…</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="px-3 pt-2 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="chatbot-quick-actions">
            {QUICK_ACTIONS.map((a) => (
              <button key={a.to} data-testid={`quick-action-${a.to.replace(/\//g, "")}`}
                onClick={() => { navigate(a.to); setOpen(false); }}
                className="shrink-0 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 hover:bg-primary/20 transition-colors whitespace-nowrap">
                {a.label}
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-border flex items-center gap-2">
            <input data-testid="chatbot-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a website, ads, repair…"
              className="flex-1 bg-secondary rounded-full px-4 h-11 text-sm outline-none focus:ring-2 ring-primary" />
            <button data-testid="chatbot-send" onClick={send} disabled={busy}
              className="h-11 w-11 grid place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
